import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { RemoteError, SignInRequired, type FoundBusiness, type Me } from '@nandanam/sync';

/**
 * Microsoft sign-in (personal Microsoft accounts) with MSAL.js. Uses full-page redirects rather than
 * pop-ups, because pop-ups are unreliable in an iPhone/iPad home-screen app.
 *
 * The client ID is the public "Application (client) ID" of the "Nandanam Farms" app registration. It is
 * not a secret. There is no client secret anywhere in this app.
 */
export const CLIENT_ID: string = import.meta.env.VITE_MS_CLIENT_ID || '9f47dee4-4c93-49a4-ac3b-71125dfbd860';
const SCOPES = ['User.Read', 'Files.ReadWrite.All'];

let pca: PublicClientApplication | null = null;

/** Finishes a sign-in that just came back from Microsoft, and returns the signed-in account if any. */
export async function initAuth(): Promise<AccountInfo | null> {
  pca = new PublicClientApplication({
    auth: { clientId: CLIENT_ID, authority: 'https://login.microsoftonline.com/consumers', redirectUri: location.origin, postLogoutRedirectUri: location.origin },
    cache: { cacheLocation: 'localStorage' },
  });
  await pca.initialize();
  const res = await pca.handleRedirectPromise();
  if (res?.account) pca.setActiveAccount(res.account);
  const acc = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
  if (acc) pca.setActiveAccount(acc);
  return acc;
}

export function signIn(): Promise<void> {
  return pca!.loginRedirect({ scopes: SCOPES, prompt: 'select_account' });
}

/** Forget the sign-in on this device only (does not sign the person out of Microsoft everywhere). */
export async function signOutHere(): Promise<void> {
  const account = pca?.getActiveAccount();
  if (account) await pca!.clearCache({ account });
}

export async function getToken(): Promise<string> {
  const account = pca?.getActiveAccount();
  if (!pca || !account) throw new SignInRequired('Not signed in');
  try {
    return (await pca.acquireTokenSilent({ scopes: SCOPES, account })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) throw new SignInRequired('Sign-in expired — sign in again');
    const code = (e as any)?.errorCode ?? '';
    if (!navigator.onLine || /network/i.test(code)) throw new RemoteError('offline', `No connection to Microsoft sign-in (${code || (e as any)?.message || 'unknown'})`);
    throw new SignInRequired(`Sign-in problem (${code || 'unknown'}) — sign in again`);
  }
}

/* The business folder chosen on this device, kept per Microsoft account so the app can open offline. */
export interface Saved { me: Me; biz: FoundBusiness }
const key = (a: AccountInfo) => `nf-business:${a.homeAccountId}`;
export function loadSaved(a: AccountInfo): Saved | null {
  try { const v = localStorage.getItem(key(a)); return v ? JSON.parse(v) : null; } catch { return null; }
}
export function saveChosen(a: AccountInfo, s: Saved) { localStorage.setItem(key(a), JSON.stringify(s)); }
export function forgetChosen(a: AccountInfo) { localStorage.removeItem(key(a)); }
export function activeAccount(): AccountInfo | null { return pca?.getActiveAccount() ?? null; }

/** 'demo' while someone is trying the demo; otherwise the app asks for Microsoft sign-in. */
export const getMode = () => { try { return localStorage.getItem('nf-mode'); } catch { return null; } };
export const setMode = (m: 'demo' | null) => { try { if (m) localStorage.setItem('nf-mode', m); else localStorage.removeItem('nf-mode'); } catch { /* private mode */ } };
